import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, DocumentType } from '../types';
import {
    format,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    isSameMonth,
    isSameDay,
    addMonths,
    subMonths,
    isToday,
    isBefore
} from 'date-fns';
import { el, enUS } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, FileText, CheckCircle, Clock, AlertCircle } from 'lucide-react';

interface ClientObligationsCalendarProps {
    documents: Document[];
    documentTypes: DocumentType[];
}

export const ClientObligationsCalendar: React.FC<ClientObligationsCalendarProps> = ({ documents, documentTypes }) => {
    const { t, i18n } = useTranslation(['dashboard']);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());

    const locale = i18n.language === 'el' ? el : enUS;

    // Filter and Map Obligations
    const obligations = useMemo(() => {
        return documents.filter(doc => {
            if (!doc.ai_analysis) return false;

            // Check AI OCR Permission
            if (documentTypes && documentTypes.length > 0) {
                const typeConfig = documentTypes.find(t => t.id === doc.typeId);
                if (typeConfig && !typeConfig.aiOcrEnabled) {
                    return false;
                }
            }

            // Custom Exclusion: Hide Invoices / Τιμολόγια (Income)
            // Keeping consistent with Financial Table: exclude 'invoice' types
            const typeLower = (doc.typeName || '').toLowerCase();
            if (typeLower.includes('invoice') || typeLower.includes('τιμολογ')) return false;
            return true;
        }).map(doc => {
            const dateStr = doc.ai_analysis?.installment_info?.installment_date || doc.ai_analysis?.doc_date;
            const date = dateStr ? new Date(dateStr) : null;
            const amount = doc.ai_analysis?.installment_info?.total_debt_amount || 0;
            // Use real payment status from document, default to false if undefined
            const isPaid = doc.paymentStatus === 'paid';
            const isOverdue = date && isBefore(date, new Date()) && !isPaid;

            return {
                id: doc.id,
                title: doc.ai_analysis?.details?.issuer || doc.title || 'Unknown',
                amount,
                date,
                doc,
                status: isPaid ? 'paid' : (isOverdue ? 'overdue' : 'pending')
            };
        }).filter(o => o.date !== null) as {
            id: string,
            title: string,
            amount: number,
            date: Date,
            doc: Document,
            status: 'paid' | 'pending' | 'overdue'
        }[];
    }, [documents, documentTypes]);

    // Calendar Generation
    const days = useMemo(() => {
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(monthStart);
        // Force start of week to Monday for Greece/Europe widely, or use locale default
        const startDate = startOfWeek(monthStart, { locale });
        const endDate = endOfWeek(monthEnd, { locale });

        return eachDayOfInterval({ start: startDate, end: endDate });
    }, [currentMonth, locale]);

    // Handlers
    const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
    const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

    // Get events for specific day
    const getEventsForDay = (day: Date) => obligations.filter(o => isSameDay(o.date, day));

    const selectedDayEvents = getEventsForDay(selectedDate);

    // Header for Grid Days
    const weekDays = useMemo(() => {
        const start = startOfWeek(new Date(), { locale });
        return Array.from({ length: 7 }).map((_, i) => {
            const day = new Date(start);
            day.setDate(day.getDate() + i);
            return format(day, 'EEE', { locale });
        });
    }, [locale]);

    return (
        <div className="flex flex-col lg:flex-row gap-6 h-[calc(100vh-140px)] animate-fadeIn">
            {/* Calendar Section */}
            <div className="flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                    <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2 capitalize">
                        <CalendarIcon className="text-blue-600" size={20} />
                        {format(currentMonth, 'MMMM yyyy', { locale })}
                    </h2>
                    <div className="flex gap-2">
                        <button onClick={prevMonth} className="p-1 hover:bg-slate-100 rounded-lg"><ChevronLeft /></button>
                        <button onClick={() => { setCurrentMonth(new Date()); setSelectedDate(new Date()); }} className="text-xs font-bold px-3 py-1 bg-blue-50 text-blue-600 rounded-lg">Today</button>
                        <button onClick={nextMonth} className="p-1 hover:bg-slate-100 rounded-lg"><ChevronRight /></button>
                    </div>
                </div>

                {/* Grid Header */}
                <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50">
                    {weekDays.map((day, i) => (
                        <div key={i} className="p-3 text-center text-xs font-bold text-slate-400 uppercase">
                            {day}
                        </div>
                    ))}
                </div>

                {/* Grid Body */}
                <div className="flex-1 grid grid-cols-7 auto-rows-fr bg-slate-100 gap-px border-b border-slate-200">
                    {days.map((day, idx) => {
                        const dayEvents = getEventsForDay(day);
                        const isSelected = isSameDay(day, selectedDate);
                        const isCurrentMonth = isSameMonth(day, currentMonth);
                        const isTodayDate = isToday(day);

                        return (
                            <div
                                key={day.toString()}
                                onClick={() => setSelectedDate(day)}
                                className={`bg-white relative p-2 cursor-pointer transition-colors hover:bg-blue-50 min-h-[80px]
                                    ${!isCurrentMonth ? 'text-slate-300 bg-slate-50/50' : 'text-slate-700'}
                                    ${isSelected ? 'ring-2 ring-inset ring-blue-500 z-10' : ''}
                                `}
                            >
                                <span className={`text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full
                                    ${isTodayDate ? 'bg-blue-600 text-white' : ''}
                                `}>
                                    {format(day, 'd')}
                                </span>

                                {/* Indicators */}
                                <div className="mt-1 space-y-1">
                                    {dayEvents.slice(0, 3).map((ev, i) => (
                                        <div key={i} className={`text-[10px] truncate px-1.5 py-0.5 rounded-md font-medium border
                                            ${ev.status === 'overdue' ? 'bg-red-50 text-red-700 border-red-100' :
                                                ev.status === 'paid' ? 'bg-green-100 text-green-800 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-100'}
                                        `}>
                                            {ev.title}
                                        </div>
                                    ))}
                                    {dayEvents.length > 3 && (
                                        <div className="text-[10px] text-slate-400 pl-1">+{dayEvents.length - 3} more</div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Side Panel: Daily Details */}
            <div className="w-full lg:w-80 bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col overflow-hidden">
                <div className="p-4 border-b border-slate-100 bg-block">
                    <h3 className="font-bold text-slate-900 capitalize">
                        {format(selectedDate, 'EEEE, d MMMM', { locale })}
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">
                        {selectedDayEvents.length} {t('obligations.details')}
                    </p>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {selectedDayEvents.length > 0 ? (
                        selectedDayEvents.map(ev => (
                            <div key={ev.id} className="p-3 border border-slate-100 rounded-xl hover:shadow-sm transition-shadow bg-white group">
                                <div className="flex justify-between items-start mb-2">
                                    <div className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1
                                        ${ev.status === 'overdue' ? 'bg-red-100 text-red-700' :
                                            ev.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}
                                    `}>
                                        {ev.status === 'overdue' ? <AlertCircle size={10} /> : ev.status === 'paid' ? <CheckCircle size={10} /> : <Clock size={10} />}
                                        {t(`obligations.status.${ev.status}`)}
                                    </div>
                                    <span className="font-mono text-sm font-bold text-slate-900">
                                        {new Intl.NumberFormat(i18n.language === 'el' ? 'el-GR' : 'en-US', { style: 'currency', currency: 'EUR' }).format(ev.amount)}
                                    </span>
                                </div>
                                <h4 className="font-medium text-slate-800 text-sm mb-1 line-clamp-1" title={ev.title}>{ev.title}</h4>
                                <p className="text-xs text-slate-400 capitalize">{ev.doc.typeName}</p>
                                <a href={ev.doc.fileUrl} target="_blank" rel="noreferrer" className="text-[10px] text-blue-600 font-bold mt-2 inline-flex items-center gap-1 hover:underline">
                                    <FileText size={10} /> View Document
                                </a>
                            </div>
                        ))
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-slate-400 py-10">
                            <Clock size={32} className="mb-2 opacity-20" />
                            <p className="text-sm text-center px-4">{t('obligations.empty_day')}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
